import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex } from "antd";
import { ColumnType } from "antd/es/table/interface";
import { Link } from "react-router-dom";
import { DeleteOutlined, RedoOutlined } from "@ant-design/icons";

import { useResponsive } from "@/shared/hooks/useBreakpoints";
import {
  useDeleteDataBlockRequestMutation,
  useLazyGetDataBlockRequestsQuery,
} from "@/redux/service";
import UserAvatar from "@/domains/users/components/UserAvatar";
import QueryTable from "@/components_v2/Tables/QueryTable";
import BaseAvatar from "@/components_v2/Avatar/BaseAvatar";
import RequestFilters from "./RequestFilters";
import EditRequestModal from "@/domains/evidence-requests/components/EditRequestModal";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import {
  BaseDataTemplateResponse,
  DataBlockRequest,
} from "@/redux/datablocks/types";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import { RequestStatuses } from "@/domains/evidence-requests/constants";
import {
  DataBlockRequestFiltersState,
  DataBlockRequestsTableProps,
} from "@/domains/evidence-requests/types";

import "../styles.css";

const RequestsTable = ({
  columnNames,
  filterNames,
  defaultFilters = {},
  additionalColumns,
  ...props
}: DataBlockRequestsTableProps<DataBlockRequest>) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();

  const [filters, setFilters] =
    useState<Partial<DataBlockRequestFiltersState>>(defaultFilters);
  const [deleteDataBlockRequest] = useDeleteDataBlockRequestMutation();
  const [editRequestModal, setEditRequestModal] = useState<{
    open: boolean;
    request: DataBlockRequest | undefined;
  }>({
    open: false,
    request: undefined,
  });

  const columns: Record<string, ColumnType<DataBlockRequest>> = useMemo(() => {
    const predefinedColumns: Record<string, ColumnType<DataBlockRequest>> = {
      user: {
        title: t("datablocks.labels.user"),
        fixed: !isMobile ? "left" : undefined,
        width: 200,
        ellipsis: true,
        render: (value: DataBlockRequest) => {
          let avatar;
          let displayName;
          if (value.user) {
            avatar = <UserAvatar user={value.user} />;
            displayName = value.user.fullName;
          } else if (value.userInvite) {
            avatar = (
              <BaseAvatar
                label={value.userInvite.email.slice(0, 2).toUpperCase()}
              />
            );
            displayName = value.userInvite.email;
          }

          return (
            <p>
              {avatar} {displayName}
            </p>
          );
        },
      },
      type: {
        title: t("table.dataTemplateType"),
        dataIndex: "dataTemplate",
        render: (value: BaseDataTemplateResponse) => {
          return value.title;
        },
      },
      status: {
        title: t("datablocks.labels.status"),
        dataIndex: "status",
        render: (value: string) => {
          return RequestStatuses[value as keyof typeof RequestStatuses];
        },
      },
      dataBlock: {
        title: t("datablocks.labels.dataBlock"),
        dataIndex: "dataBlock",
        render: (value) => {
          return value ? (
            <Link
              to={`/evidence/${value}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("datablocks.labels.seeDataBlock")}
            </Link>
          ) : (
            "-"
          );
        },
      },
      actions: {
        title: t("datablocks.labels.actions"),
        key: "actions",
        render: (value: DataBlockRequest) => (
          <div className="requests-table-actions-container">
            <BaseTooltip
              title={t("datablocks.labels.delete")}
              className="requests-table-actions-tooltip-button"
            >
              <BaseButton
                size={BUTTON_SIZES.SMALL}
                variant={BUTTON_VARIANTS.LINK}
                onClick={() => deleteDataBlockRequest(value.id)}
              >
                <DeleteOutlined />
              </BaseButton>
            </BaseTooltip>

            {value.dataBlock && (
              <BaseTooltip
                title={t("datablocks.labels.resend")}
                className="requests-table-actions-tooltip-button"
              >
                <BaseButton
                  size={BUTTON_SIZES.SMALL}
                  variant={BUTTON_VARIANTS.LINK}
                  onClick={() =>
                    setEditRequestModal({ open: true, request: value })
                  }
                >
                  <RedoOutlined />
                </BaseButton>
              </BaseTooltip>
            )}
          </div>
        ),
      },
    };
    return { ...predefinedColumns, ...(additionalColumns || {}) };
  }, [isMobile]);

  const mappedColumns = useMemo(
    () =>
      columnNames.map(
        (columnKey) => columns[columnKey] as ColumnType<DataBlockRequest>,
      ),
    [columns, columnNames],
  );

  return (
    <Flex vertical gap={"small"}>
      <RequestFilters
        filterNames={filterNames}
        setFilters={setFilters}
        defaultFilters={defaultFilters}
      />
      <QueryTable<DataBlockRequest>
        query={useLazyGetDataBlockRequestsQuery}
        filters={filters}
        initialSorting={{
          field: "created_at",
          order: "descend",
        }}
        columns={mappedColumns}
        locale={{
          emptyText: (
            <div>
              {t("datablocks.labels.noActiveRequests")}
              {t("datablocks.labels.sendRequestsToColleagues")}
            </div>
          ),
        }}
        {...props}
      />
      <EditRequestModal
        open={editRequestModal.open}
        setOpen={(value) =>
          setEditRequestModal({ open: value, request: undefined })
        }
        request={editRequestModal.request?.id}
      />
    </Flex>
  );
};

export default RequestsTable;
